import { FormFieldsGeneratorService } from './../../form-helpers/form-fields-generator.service';
import { AfterContentInit, Component, Input, OnChanges } from '@angular/core';
import { DeliveryPickupService } from '../delivery-pickup.service';
import PaymentMode from '../../../models/cartDetails/paymentMode.model';
import { PriceTypeEnum } from '../../../models/cartDetails/priceType.model';
import { SummaryPricesGroupModel } from '../../../customer-offer/summary/summary-prices-group.model';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../../external-interfaces/notification-provider.service';
import { CommonInfoService } from '../../../cart-process/common-info.service';
import { PaymentModeTypeEnum } from '../../../models/cartDetails/paymentModeType.model';
import { FormStateChange, FormStateChangeService, StateInputFields } from '../../form-state/form-state-change.service';
import { DeliveryFormOutputState, DeliveryFormStateService } from '../../form-state/delivery-form-state.service';
import { FormFieldsMasks } from '../../field-mask/form-fields-masks';
import { FormGroup } from '@angular/forms';
import { UserDataFormService } from '../../user-data/user-data-form.service';
import { IPrice } from '../../../interfaces/cart-details.interface';
import { FormSectionTypeEnum, FormStepsService } from '../../form-steps/form-steps.service';
import { hiddenElementSlideInOut } from '../../../utils/animations/slides.animation';
import RecaptchaService from '../../recaptcha/recaptcha.service';
import { DataLayerService } from 'src/ng2-app/scripts/customer-actions/data-layer.service';

declare var P4: any;

@Component({
  selector: 'payment-mode-component',
  templateUrl: './payment-mode.component.html',
  animations: [hiddenElementSlideInOut]
})
export class PaymentModeComponent implements OnChanges, AfterContentInit {
  @Input() availablePaymentModes: PaymentMode[];

  public visibility: DeliveryFormOutputState;
  public selectedPaymentModeCode: string;
  public hideSection = false;
  public isB2BOffer = false;
  public formFields: any;
  public formGroups: { [s: string]: FormGroup } = {};
  public formSectionTypeEnum: typeof FormSectionTypeEnum = FormSectionTypeEnum;
  public activeForm: FormGroup;

  protected paymentModes: PaymentMode[];
  private summaryPrices: SummaryPricesGroupModel[];
  private identityConfirmationType = '';
  private deliveryCost: IPrice = { net: '0.00',  gross: '0.00' };

  constructor(
    public formStepsService: FormStepsService,
    public validationMasks: FormFieldsMasks,
    private deliveryPickupService: DeliveryPickupService,
    private notificationProvider: NotificationProviderService,
    private formStateChangeService: FormStateChangeService,
    private deliveryFormState: DeliveryFormStateService,
    private userDataFormService: UserDataFormService,
    private commonInfoService: CommonInfoService,
    private recaptchaService: RecaptchaService,
    private dataLayerService: DataLayerService,
    private formsGenerator: FormFieldsGeneratorService
  ) {
    this.formFields = this.userDataFormService.formFields;

    this.visibility = deliveryFormState.outputState;

    this.formsGenerator.watchActiveForm().subscribe(form => {
      this.activeForm = form;
    });

    this.deliveryFormState.subscribe((deliveryFormOutputState: DeliveryFormOutputState): void => {
      this.visibility = deliveryFormOutputState;

      if (deliveryFormOutputState.isDeliveryValid) {
        this.formStepsService.openNextSection(FormSectionTypeEnum.DELIVERY);

        if (this.paymentModes.length === 1 || (!this.paymentModes && this.availablePaymentModes.length === 1)) {
          this.clickFirstAvailablePaymentMode();
        }

        if (this.hideSection) {
          this.formStepsService.openNextSection(FormSectionTypeEnum.RECAPTCHA);
          this.clickFirstAvailablePaymentMode();
        }
      } else {
        if (this.formStepsService.getSection(FormSectionTypeEnum.RECAPTCHA).isOpen) this.recaptchaService.reset();
        if (this.selectedPaymentModeCode !== PaymentModeTypeEnum.EMAIL_COD) this.formStepsService.closeSections(FormSectionTypeEnum.PAYMENT);
        if (!this.hideSection) this.activeForm.controls.paymentMode.reset();
      }
    });
  }

  ngOnChanges(): void {
    this.preparePaymentModesData();

    this.notificationProvider.subscribe(NotificationProviderTypeEnum.COMMON_INFO, (commonInfo: CommonInfoService): void => {
      if (!commonInfo) {
        return;
      }

      this.isB2BOffer = !commonInfo.b2c;

      this.preparePaymentModes();
    });

    if (this.availablePaymentModes && !this.selectedPaymentModeCode) {
      if (this.paymentModes.length === 1 || (!this.paymentModes && this.availablePaymentModes.length === 1)) {
        this.clickFirstAvailablePaymentMode();
      }
    }

    this.deliveryPickupService.subscribePaymentChange((paymentModes: PaymentMode[]): void => {
      this.preparePaymentModesClass(paymentModes);
      this.preparePaymentModes();
    });

    this.notificationProvider.subscribe(
      NotificationProviderTypeEnum.SUMMARY_PRICES,
      (summaryPrices: any): void => {
        if (!summaryPrices) {
          return;
        }

        this.summaryPrices = summaryPrices.summaryPrices.summaryPricesList;

        this.updateVisibility();
      });

    this.formStateChangeService.subscribe((stateChange: FormStateChange): void => {
      if (stateChange.inputField === StateInputFields.IDENTITY_CONFIRMATION_TYPE) {
        this.identityConfirmationType = stateChange.value;
        this.preparePaymentModesData();
      }

      if (stateChange.inputField === StateInputFields.POS_PAYMENT_MODES) {
        this.preparePaymentModesData(stateChange.value);
      }

      if (stateChange.inputField === StateInputFields.DELIVERY_COST) {
        this.deliveryCost = stateChange.value;
        this.updateVisibility();
      }

      if (stateChange.inputField === StateInputFields.PAYMENT_MODE_VALUE_NAME) {
        this.selectedPaymentModeCode = stateChange.value;
      }
    });

    this.deliveryFormState.subscribe(
      (deliveryFormOutputState: DeliveryFormOutputState): void => {
        if (deliveryFormOutputState.isDeliveryValid && deliveryFormOutputState.isPaymentValid && this.activeForm.get('paymentMode').value) {
          this.formStepsService.openNextSection(FormSectionTypeEnum.PAYMENT);
          if (this.commonInfoService.isOtherSalesOffer && this.recaptchaService.mockRecaptcha) this.formStepsService.openNextSection(FormSectionTypeEnum.RECAPTCHA);
        } else {
          if (this.formStepsService.getSection(FormSectionTypeEnum.RECAPTCHA).isOpen) this.recaptchaService.reset();
          this.formStepsService.getSection(FormSectionTypeEnum.RECAPTCHA).isOpen = false;
          if (!this.hideSection && !this.activeForm.get('paymentMode')?.value) {
            this.formStepsService.getSection(FormSectionTypeEnum.SUMMARY).isOpen = false;
          } else if (this.activeForm.valid) {
            this.formStepsService.getSection(FormSectionTypeEnum.SUMMARY).isOpen = true;
          }
        }
      });

    setTimeout((): void => {
      P4.modules.cartAddress._bindEvents();
    });
  }

  ngAfterContentInit(): void {
    this.formStepsService.setSectionVisibility(FormSectionTypeEnum.PAYMENT);
  }

  public changePaymentMode(): void {
    this.selectedPaymentModeCode = this.activeForm.get('paymentMode').value;

    this.formStateChangeService.setField({
      inputField: this.formStateChangeService.StateInputFields.PAYMENT_MODE_VALUE_NAME,
      value: this.selectedPaymentModeCode
    });

    if (!this.commonInfoService.isOtherSalesOffer) {
      this.formStepsService.openNextSection(FormSectionTypeEnum.RECAPTCHA);
    }

    const selectedPaymentModeDesc: string = this.paymentModes.find((item: PaymentMode): boolean => item.code === this.selectedPaymentModeCode)?.description;
    if (selectedPaymentModeDesc) {
      this.dataLayerService.setDeliveryPageEvent(selectedPaymentModeDesc, 'add_payment_info');
    }
  }

  private preparePaymentModesData(paymentMode?: PaymentMode[]): void {
    const paymentModes: PaymentMode[] = paymentMode ? paymentMode : this.availablePaymentModes;

    this.preparePaymentModesClass(paymentModes);
    this.preparePaymentModes();
    this.prepareFormControls();

    this.activeForm?.get('paymentMode').setValue(this.selectedPaymentModeCode);
  }


  private updateVisibility(): void {
    if (this.summaryPrices) {
      const payNow: SummaryPricesGroupModel = this.summaryPrices.find((price: SummaryPricesGroupModel): boolean => price.type === PriceTypeEnum.PAY_NOW);

      this.hideSection = !payNow || parseFloat(payNow.getMainPrice()) === 0 && +this.deliveryCost[this.commonInfoService.taxName] === 0;
    }
  }

  private preparePaymentModesClass(paymentModes: any): void {
    this.paymentModes = [];

    paymentModes?.forEach((paymentMode: any): void => {
      this.paymentModes.push(new PaymentMode(paymentMode));
    });
  }

  private changeVisibilityForPaymentMode(): void {
    const paymentModes: PaymentMode = this.paymentModes.find((paymentMode: PaymentMode): boolean => paymentMode.code === PaymentModeTypeEnum.COURIER_COD_CREDIT);

    if (!paymentModes) {
      return;
    }

    paymentModes.setChangedVisibility(!this.isB2BOffer);
  }

  private preparePaymentModes(): void {
    this.changeVisibilityForPaymentMode();

    this.paymentModes.forEach((paymentMode: PaymentMode): void => {
      if (this.isB2BOffer) {
        paymentMode.infoText = 'Za zakupione urządzenia otrzymasz Fakturę VAT. Jeśli Twoje zamówienie jest mniejsze niż 450&nbsp;zł -&nbsp;jako dowód sprzedaży wystawimy paragon z&nbsp;numerem NIP nabywcy, który jest fakturą uproszczoną.';

        if (!this.commonInfoService.isOtherSalesOffer) {
          paymentMode.infoTextSubLine = 'Dane do faktury zostaną pobrane automatycznie z&nbsp;danych do umowy.';
        }
      } else if (paymentMode.code.includes('CREDIT')) {
        paymentMode.infoText = 'Za zakupione urządzenia otrzymasz Fakturę';
      } else {
        paymentMode.infoText = 'Za zakupione urządzenia otrzymasz paragon';
      }
    });
  }

  private prepareFormControls(): void {
    this.formsGenerator.preparePaymentOptions();
  }

  private clickFirstAvailablePaymentMode(): void {
    const paymentMode = this.paymentModes ? this.paymentModes[0] : this.availablePaymentModes[0];

    this.activeForm?.get('paymentMode')?.setValue(paymentMode.code);
  }
}


